import React, { useState } from 'react';

const CEStatsTable = ({ data }) => {
  const [sortConfig, setSortConfig] = useState({
    key: 'completion_rate',
    direction: 'descending'
  });

  // Handle sorting
  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  // Sort data
  const sortedData = React.useMemo(() => {
    if (!data) return [];
    
    const sortableData = [...data];
    if (sortConfig.key) {
      sortableData.sort((a, b) => {
        const aValue = a[sortConfig.key] || 0;
        const bValue = b[sortConfig.key] || 0;
        
        if (aValue < bValue) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableData;
  }, [data, sortConfig]);

  // Get sort indicator
  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) return '';
    return sortConfig.direction === 'ascending' ? ' Asc' : ' Desc';
  };

  // Performance indicator
  const getPerformanceIndicator = (rate) => {
    if (rate >= 90) return 'excellent';
    if (rate >= 75) return 'good';
    if (rate >= 50) return 'average';
    return 'poor';
  };

  // Level badge color
  const getLevelBadgeClass = (level) => {
    switch (level.toLowerCase()) {
      case 'premium': return 'premium';
      case 'critical': return 'critical';
      case 'intermediate': return 'intermediate';
      case 'non_critical': return 'non-critical';
      default: return 'unassigned';
    }
  };

  if (!data || data.length === 0) {
    return (
      <div className="stats-table-container">
        <div className="no-data">
          <p>No CE performance data available for the selected period.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="stats-table-container">
      <div className="table-info">
        <p>Showing {sortedData.length} CEs</p>
      </div>
      
      <div className="table-wrapper">
        <table className="stats-table ce-stats-table">
          <thead>
            <tr>
              <th 
                className="sortable"
                onClick={() => handleSort('ce_name')}
              >
                CE Name{getSortIndicator('ce_name')}
              </th>
              <th 
                className="sortable"
                onClick={() => handleSort('ce_email')}
              >
                Email{getSortIndicator('ce_email')}
              </th>
              <th 
                className="sortable"
                onClick={() => handleSort('ce_level')}
              >
                Level{getSortIndicator('ce_level')}
              </th>
              <th 
                className="sortable number"
                onClick={() => handleSort('assigned')}
              >
                Assigned{getSortIndicator('assigned')}
              </th>
              <th 
                className="sortable number"
                onClick={() => handleSort('completed')}
              >
                Completed{getSortIndicator('completed')}
              </th>
              <th 
                className="sortable number"
                onClick={() => handleSort('wip')}
              >
                WIP{getSortIndicator('wip')}
              </th>
              <th 
                className="sortable number"
                onClick={() => handleSort('completion_rate')}
              >
                Completion Rate{getSortIndicator('completion_rate')}
              </th>
              <th 
                className="sortable number"
                onClick={() => handleSort('total_word_count')}
              >
                Word Count{getSortIndicator('total_word_count')}
              </th>
              <th 
                className="sortable number"
                onClick={() => handleSort('avg_turnaround_days')}
              >
                Avg Turnover{getSortIndicator('avg_turnaround_days')}
              </th>
              <th 
                className="sortable number"
                onClick={() => handleSort('productivity_words_per_day')}
              >
                Productivity{getSortIndicator('productivity_words_per_day')}
              </th>
              <th 
                className="sortable number"
                onClick={() => handleSort('daily_capacity_pages')}
              >
                Capacity{getSortIndicator('daily_capacity_pages')}
              </th>
              <th 
                className="sortable"
                onClick={() => handleSort('is_backup')}
              >
                Backup{getSortIndicator('is_backup')}
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedData.map((ce, index) => (
              <tr key={ce.ce_id} className="stats-row">
                <td className="name-cell">
                  <div className="ce-name">{ce.ce_name}</div>
                </td>
                <td className="email-cell">
                  <div className="ce-email">{ce.ce_email}</div>
                </td>
                <td className="level-cell">
                  <span className={`level-badge ${getLevelBadgeClass(ce.ce_level)}`}>
                    {ce.ce_level}
                  </span>
                </td>
                <td className="number-cell">
                  <span className="stat-value assigned">{ce.assigned}</span>
                </td>
                <td className="number-cell">
                  <span className="stat-value completed">{ce.completed}</span>
                </td>
                <td className="number-cell">
                  <span className="stat-value wip">{ce.wip}</span>
                </td>
                <td className="number-cell">
                  <span className={`stat-value rate ${getPerformanceIndicator(ce.completion_rate)}`}>
                    {ce.completion_rate}%
                  </span>
                </td>
                <td className="number-cell">
                  <span className="stat-value word-count">
                    {ce.total_word_count.toLocaleString()}
                  </span>
                </td>
                <td className="number-cell">
                  <span className="stat-value turnaround">
                    {ce.avg_turnaround_days}d
                  </span>
                </td>
                <td className="number-cell">
                  <span className="stat-value productivity">
                    {ce.productivity_words_per_day.toLocaleString()}
                  </span>
                </td>
                <td className="number-cell">
                  <span className="stat-value capacity">
                    {ce.daily_capacity_pages}
                  </span>
                </td>
                <td className="backup-cell">
                  <span className={`backup-indicator ${ce.is_backup ? 'yes' : 'no'}`}>
                    {ce.is_backup ? 'Yes' : 'No'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="table-legend">
        <h4>Performance Indicators:</h4>
        <div className="legend-items">
          <div className="legend-item excellent">
            <span className="indicator"></span>
            <span>Excellent (90%+)</span>
          </div>
          <div className="legend-item good">
            <span className="indicator"></span>
            <span>Good (75-89%)</span>
          </div>
          <div className="legend-item average">
            <span className="indicator"></span>
            <span>Average (50-74%)</span>
          </div>
          <div className="legend-item poor">
            <span className="indicator"></span>
            <span>Poor (&lt;50%)</span>
          </div>
        </div>
        
        <h4>CE Levels:</h4>
        <div className="legend-items">
          <div className="legend-item premium">
            <span className="indicator"></span>
            <span>Premium</span>
          </div>
          <div className="legend-item critical">
            <span className="indicator"></span>
            <span>Critical</span>
          </div>
          <div className="legend-item intermediate">
            <span className="indicator"></span>
            <span>Intermediate</span>
          </div>
          <div className="legend-item non-critical">
            <span className="indicator"></span>
            <span>Non-Critical</span>
          </div>
          <div className="legend-item unassigned">
            <span className="indicator"></span>
            <span>Unassigned</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CEStatsTable;
